import { usePrevious } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";

import { useUserContext } from "@/context/user.context";
import {
  clearLocalStorageTableState,
  FILTER_PARAMS_LOCAL_STORAGE_BASE_KEY,
  FILTER_VERSION,
  filterVersionLocalStorageKey,
  makeLocalStorageKeyByUser,
} from "@/utils/local-storage";

import type { AssertValidParams, ParamsConstraint } from "./filter-params";
import { useFilterParams, type FilterParamsHandle } from "./use-filter-params";

interface UsePersistedFilterParamsResult<T extends ParamsConstraint> {
  handle: FilterParamsHandle<T>;
  initialized: boolean;
}

const PAGE_KEY = "page";

export const usePersistedFilterParams = <T extends ParamsConstraint>(
  storageKeyPrefix: string,
  defaults: Partial<T & AssertValidParams<T>>,
): UsePersistedFilterParamsResult<T> => {
  const pageKey = PAGE_KEY as keyof T;
  const { user } = useUserContext();
  const previousUser = usePrevious(user);
  const [initialized, setInitialized] = useState(false);

  const storageKey = useMemo(
    () =>
      makeLocalStorageKeyByUser(
        `${storageKeyPrefix}${FILTER_PARAMS_LOCAL_STORAGE_BASE_KEY}`,
        user?.id,
      ),
    [storageKeyPrefix, user?.id],
  );

  const baseHandle = useFilterParams<T>(defaults);

  // Wrap handle to auto-reset page when modifying other keys
  const handle: FilterParamsHandle<T> = useMemo(() => {
    const resetPageIfNeeded = (key: keyof T) => {
      if (key !== pageKey) {
        baseHandle.set(pageKey, 1 as T[keyof T]);
      }
    };

    return {
      ...baseHandle,
      set: (key, value) => {
        resetPageIfNeeded(key);
        baseHandle.set(key, value);
        return handle;
      },
      append: (key, value) => {
        resetPageIfNeeded(key);
        baseHandle.append(key, value);
        return handle;
      },
      delete: ((key: keyof T, value?: unknown) => {
        resetPageIfNeeded(key);
        if (value === undefined) {
          baseHandle.delete(key);
        } else {
          (baseHandle.delete as (k: keyof T, v: unknown) => void)(key, value);
        }
        return handle;
      }) as FilterParamsHandle<T>["delete"],
      toggleValue: (key, value) => {
        resetPageIfNeeded(key);
        baseHandle.toggleValue(key, value);
        return handle;
      },
      clear: () => {
        baseHandle.clear();
        baseHandle.set(pageKey, 1 as T[keyof T]);
        return handle;
      },
    };
  }, [baseHandle, pageKey]);

  const handleString = handle.toString();

  // Initialize from storage on first user load
  useEffect(() => {
    if (initialized || !user || previousUser) {
      return;
    }

    const versionKey = filterVersionLocalStorageKey(user.id);
    const storedVersion = localStorage.getItem(versionKey);

    if (storedVersion !== FILTER_VERSION) {
      localStorage.setItem(versionKey, FILTER_VERSION);
      clearLocalStorageTableState(user.id);
      setInitialized(true);
      return;
    }

    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        baseHandle.replaceAll(parsed);
      } catch (error) {
        if (error instanceof Error) {
          window.newrelic?.noticeError(error, {
            description: "Failed to parse stored filter params",
          });
        }
      }
    }

    setInitialized(true);
  }, [initialized, user, previousUser, storageKey, baseHandle]);

  // Persist on changes (after initialization), excluding page
  useEffect(() => {
    if (!initialized || !user?.id) {
      return;
    }

    const state = handle.toObject();
    const toStore = Object.fromEntries(
      Object.entries(state).filter(([key]) => key !== PAGE_KEY),
    );

    localStorage.setItem(storageKey, JSON.stringify(toStore));
  }, [handle, handleString, storageKey, user?.id, initialized]);

  // Reset on logout
  useEffect(() => {
    if (previousUser && !user) {
      setInitialized(false);
    }
  }, [user, previousUser]);

  return { handle, initialized };
};
